import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Card, Space, Popconfirm, message, Typography } from 'antd';
import { KeyOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../api/client';

const { Text } = Typography;

const PermissionsManager = () => {
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);

    const fetchPermissions = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/permissions');
            setPermissions(response.data);
        } catch (error) {
            message.error('Failed to fetch permissions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPermissions();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/admin/permissions/${id}`);
            message.success('Permission deleted');
            fetchPermissions();
        } catch (error) {
            message.error('Failed to delete permission');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingId) {
                await api.put(`/admin/permissions/${editingId}`, values);
                message.success('Permission updated');
            } else {
                await api.post('/admin/permissions', values);
                message.success('Permission created');
            }
            setIsModalVisible(false);
            fetchPermissions();
        } catch (error) {
            message.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Slug',
            dataIndex: 'slug',
            key: 'slug',
            render: (text) => <code style={{ background: '#f5f5f5', padding: '2px 4px', borderRadius: '4px' }}>{text}</code>
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description'
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Popconfirm
                        title="Delete permission?"
                        description="This may affect users assigned to this permission."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Card
            title={
                <Space>
                    <KeyOutlined />
                    <span>Permission Definitions</span>
                </Space>
            }
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Add Permission
                </Button>
            }
        >
            <Table
                columns={columns}
                dataSource={permissions}
                rowKey="id"
                loading={loading}
                pagination={false}
            />

            <Modal
                title={editingId ? 'Edit Permission' : 'Add Permission'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ slug: '' }}
                >
                    <Form.Item
                        name="name"
                        label="Permission Name"
                        rules={[{ required: true, message: 'Please enter a name' }]}
                    >
                        <Input placeholder="e.g., Manage Inventory" />
                    </Form.Item>

                    <Form.Item
                        name="slug"
                        label="Permission Slug"
                        rules={[
                            { required: true, message: 'Please enter a slug' },
                            { pattern: /^[a-z_]+$/, message: 'Slug must be lowercase and use underscores only' }
                        ]}
                    >
                        <Input placeholder="e.g., manage_inventory" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea placeholder="Describe what this permission allows" rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default PermissionsManager;


